import { z } from 'zod';

const usdMicrosSchema = z.number().int().min(0);
const priceVersionSchema = z.string().min(1);
const evidencePathSchema = z.array(z.string().min(1)).min(1);
const customCalculatorKeys = ['falBillableUnits'] as const;
const customCalculatorKeySchema = z.enum(customCalculatorKeys);
const policyBaseSchema = {
  priceVersion: priceVersionSchema,
  policyId: z.string().min(1).optional(),
};
const costTierSchema = z
  .object({
    value: z.string().min(1),
    amountUsdMicros: usdMicrosSchema,
  })
  .strict();
const falBillableUnitsConfigSchema = z
  .object({
    usdMicrosPerBillableUnit: usdMicrosSchema,
  })
  .strict();
const costTiersSchema = z
  .array(costTierSchema)
  .min(1, 'Cost tiers must include at least one value.')
  .superRefine((tiers, context) => {
    const seenValues = new Set<string>();
    tiers.forEach((tier, index) => {
      if (seenValues.has(tier.value)) {
        context.addIssue({
          code: 'custom',
          message: 'Cost tier values must be unique.',
          path: [index, 'value'],
        });
      }
      seenValues.add(tier.value);
    });
  });

export const adminV2ProviderCostPolicySchema = z.discriminatedUnion(
  'strategy',
  [
    z
      .object({
        strategy: z.literal('fixed'),
        amountUsdMicros: usdMicrosSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('perOutput'),
        amountUsdMicrosPerOutput: usdMicrosSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('fixedByInputParam'),
        parameterId: z.string().min(1),
        tiers: costTiersSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('providerReturnedCost'),
        amountPath: evidencePathSchema,
        unit: z.enum(['usd', 'usdMicros']),
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('providerReturnedCredits'),
        creditsPath: evidencePathSchema,
        usdMicrosPerCredit: usdMicrosSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('duration'),
        secondsPath: evidencePathSchema,
        usdMicrosPerSecond: usdMicrosSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('megapixel'),
        inputImagesParameterId: z.string().min(1).optional(),
        outputMegapixelsParameterId: z.string().min(1).optional(),
        inputMegapixelsPath: evidencePathSchema.optional(),
        outputMegapixelsPath: evidencePathSchema.optional(),
        usdMicrosPerInputMegapixel: usdMicrosSchema,
        usdMicrosPerOutputMegapixel: usdMicrosSchema,
        ...policyBaseSchema,
      })
      .strict(),
    z
      .object({
        strategy: z.literal('custom'),
        calculatorKey: customCalculatorKeySchema,
        config: falBillableUnitsConfigSchema,
        ...policyBaseSchema,
      })
      .strict(),
  ]
);

export const adminV2TokenUsageProviderCostPolicySchema = z
  .object({
    strategy: z.literal('tokenUsage'),
    inputTokensPath: evidencePathSchema,
    outputTokensPath: evidencePathSchema,
    usdMicrosPerMillionInputTokens: usdMicrosSchema,
    usdMicrosPerMillionOutputTokens: usdMicrosSchema,
    ...policyBaseSchema,
  })
  .strict();

export type AdminV2ProviderCostPolicy = z.infer<
  typeof adminV2ProviderCostPolicySchema
>;

export type AdminV2ProviderCostStrategy = AdminV2ProviderCostPolicy['strategy'];

export type AdminV2TokenUsageProviderCostPolicy = z.infer<
  typeof adminV2TokenUsageProviderCostPolicySchema
>;

export type AdminV2ProviderCostCustomCalculatorKey =
  (typeof customCalculatorKeys)[number];

export function isAdminV2ProviderCostCustomCalculatorKey(
  key: string
): key is AdminV2ProviderCostCustomCalculatorKey {
  return customCalculatorKeys.includes(
    key as AdminV2ProviderCostCustomCalculatorKey
  );
}
